import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CtaBand } from "../../../components/site/CtaBand";
import { Lightbox, type LightboxItem } from "../../../components/site/Lightbox";
import { PageHead } from "../../../components/site/PageHead";
import { PRODUCTS, type Product } from "../../../content/products";
import { IMAGES } from "../../../content/site";

/** One project photo in the gallery, attributed to the first product it appears under. */
export interface WorkPhoto {
  src: string;
  slug: string;
  name: string;
}

/**
 * BR-WEB-030 — the union of every product gallery, de-duplicated by image path. A photo used on two
 * legacy product pages appears once, under the first product it appears in, in range order
 * (PRODUCTS is already in range order, then legacy order).
 */
export function allWork(products: readonly Product[] = PRODUCTS): WorkPhoto[] {
  const seen = new Set<string>();
  const out: WorkPhoto[] = [];
  for (const p of products) {
    for (const src of p.images) {
      if (seen.has(src)) continue;
      seen.add(src);
      out.push({ src, slug: p.slug, name: p.name });
    }
  }
  return out;
}

const ALL_WORK = allWork();

/** A masonry tile. Holds a sandstone placeholder until loaded; a failed image is reported and dropped. */
interface WorkTileProps {
  photo: WorkPhoto;
  /** 1-based position in the filtered set, and its size — names the button like the home mosaic. */
  position: number;
  total: number;
  onOpen: () => void;
  onFail: (src: string) => void;
}

function WorkTile({ photo, position, total, onOpen, onFail }: WorkTileProps) {
  const [loaded, setLoaded] = useState(false);
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Open photo ${position} of ${total} — ${photo.name}`}
      data-testid="work-tile"
      data-product={photo.slug}
    >
      <img
        src={photo.src}
        alt={`${photo.name} project`}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => onFail(photo.src)}
        style={loaded ? undefined : { aspectRatio: "4 / 3" }}
      />
      <span className="cap" aria-hidden="true">
        {photo.name}
      </span>
    </button>
  );
}

/**
 * Our work (Story #25442) — every legacy project photo in one filterable masonry gallery.
 * Filter chips carry a per-product count and write ?product={slug} (BR-WEB-031); an unknown slug
 * falls back to All work. The lightbox (BR-WEB-022) runs over the filtered set and captions each
 * photo with its product, linking to it (BR-WEB-032).
 */
export default function OurWorkPage() {
  const [params, setParams] = useSearchParams();
  const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set());
  const [photo, setPhoto] = useState<number | null>(null);

  const all = useMemo(() => ALL_WORK.filter((w) => !failed.has(w.src)), [failed]);
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const w of all) m.set(w.slug, (m.get(w.slug) ?? 0) + 1);
    return m;
  }, [all]);

  const requested = params.get("product");
  const selected = requested && PRODUCTS.some((p) => p.slug === requested) ? requested : "all";
  const shown = useMemo(() => (selected === "all" ? all : all.filter((w) => w.slug === selected)), [all, selected]);
  const items: LightboxItem[] = useMemo(
    () => shown.map((w) => ({ src: w.src, cap: w.name, href: `/products/${w.slug}` })),
    [shown],
  );

  // A filter change is a navigation, and the site layout returns every navigation to the top of the
  // page. A chip click keeps the visitor where they were instead (they are mid-gallery).
  const keepScroll = useRef<number | null>(null);
  useEffect(() => {
    const y = keepScroll.current;
    if (y === null) return;
    keepScroll.current = null;
    const frame = requestAnimationFrame(() => window.scrollTo(0, y));
    return () => cancelAnimationFrame(frame);
  }, [selected]);

  const choose = (slug: string) => {
    if (slug === selected) return;
    keepScroll.current = window.scrollY;
    setPhoto(null);
    setParams(slug === "all" ? {} : { product: slug });
  };

  const onFail = useCallback((src: string) => {
    setFailed((prev) => (prev.has(src) ? prev : new Set(prev).add(src)));
  }, []);
  const closePhoto = useCallback(() => setPhoto(null), []);

  return (
    <>
      <PageHead
        crumbs={[{ label: "Our work" }]}
        title="Our work"
        lead="Patios, decks, opening roofs and screens we’ve designed and built for homes across Toowoomba and the Darling Downs."
        bg={IMAGES.homeBanner004}
      />
      <div className="gw-sec" style={{ paddingTop: 48 }}>
        <div className="gw-wrap">
          <div className="gw-chips" role="group" aria-label="Filter by product" style={{ marginBottom: 32 }}>
            <button type="button" className="gw-chip" aria-pressed={selected === "all"} onClick={() => choose("all")}>
              All work<span>{all.length}</span>
            </button>
            {PRODUCTS.filter((p) => counts.get(p.slug)).map((p) => (
              <button
                key={p.slug}
                type="button"
                className="gw-chip"
                aria-pressed={selected === p.slug}
                onClick={() => choose(p.slug)}
              >
                {p.name}
                <span>{counts.get(p.slug)}</span>
              </button>
            ))}
          </div>
          {shown.length ? (
            <div className="gw-masonry" data-testid="work-gallery">
              {shown.map((w, i) => (
                <WorkTile
                  key={w.src}
                  photo={w}
                  position={i + 1}
                  total={shown.length}
                  onOpen={() => setPhoto(i)}
                  onFail={onFail}
                />
              ))}
            </div>
          ) : (
            <p>
              No photos for this product yet — <Link to="/our-work">see all work</Link>.
            </p>
          )}
        </div>
      </div>
      <CtaBand />
      <Lightbox items={items} index={photo} onClose={closePhoto} onIndexChange={setPhoto} />
    </>
  );
}
